import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { applyLiveMetrics } from "@/domain/projection/importLive";
import { fetchLiveMetricsSnapshot } from "@/infrastructure/projection/fetchLiveMetrics";
import { formatPct } from "@/domain/projection/format";
import { nextAlternativeName } from "@/domain/projection/engine";
import { Button, ConfirmModal, Input, ModalShell } from "@/presentation/components/common";
import { AlternativesPanel } from "@/presentation/components/projection/AlternativesPanel";
import { CostsEditor } from "@/presentation/components/projection/CostsEditor";
import { PortfolioEditor } from "@/presentation/components/projection/PortfolioEditor";
import { PricesEditor } from "@/presentation/components/projection/PricesEditor";
import { SimulatorPanel } from "@/presentation/components/projection/SimulatorPanel";
import { useClinicReport } from "@/presentation/hooks/useClinicReport";
import { useCostProjection } from "@/presentation/hooks/useCostProjection";
import { useToast } from "@/presentation/contexts/ToastContext";

type TabKey = "simulator" | "settings" | "compare";

const TABS: TabKey[] = ["simulator", "settings", "compare"];

type SettingsSection = "costs" | "prices" | "portfolio";

export const CostProjectionPage: React.FC = () => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<TabKey>("simulator");
  const [settingsSection, setSettingsSection] = useState<SettingsSection>("costs");
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmLiveImport, setConfirmLiveImport] = useState(false);
  const [importingLive, setImportingLive] = useState(false);
  const [pendingLoadId, setPendingLoadId] = useState<string | null>(null);
  const [saveNewOpen, setSaveNewOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveNotes, setSaveNotes] = useState("");
  const { report: clinics } = useClinicReport();
  const {
    model,
    setModel,
    workspace,
    currentAlternative,
    dirty,
    result,
    breakEven,
    comparisonRows,
    resetToDefaults,
    saveCurrent,
    saveAsNew,
    loadAlternative,
    duplicateAlternative,
    renameAlternative,
    deleteAlternative,
    exportJson,
    importJson,
  } = useCostProjection();

  const handleImportLive = async () => {
    setImportingLive(true);
    try {
      const snapshot = await fetchLiveMetricsSnapshot();
      const imported = applyLiveMetrics(model, snapshot);
      setModel(imported.model);
      success(t("pages.projection.importLiveDone", {
        clinics: imported.summary.clinics,
        avanzado: formatPct(imported.summary.pctAvanzado),
        conexion: formatPct(imported.summary.pctConexion),
      }));
    } catch {
      showError(t("pages.projection.importLiveError"));
    } finally {
      setImportingLive(false);
      setConfirmLiveImport(false);
    }
  };

  const handleImport = async (file: File | undefined) => {
    if (!file) return;
    try {
      await importJson(file);
      success(t("pages.projection.imported"));
    } catch {
      showError(t("pages.projection.importError"));
    }
  };

  const requestLoad = (id: string) => {
    if (!id || id === "__unsaved__" || id === workspace.currentId) return;
    if (dirty) {
      setPendingLoadId(id);
      return;
    }
    loadAlternative(id);
    success(t("pages.projection.alternatives.loaded"));
  };

  return (
    <div className="panel-page">
      <div className="panel-page-header">
        <div>
          <h1 className="panel-page-title">{t("pages.projection.title")}</h1>
          <p className="panel-page-subtitle">{t("pages.projection.subtitle")}</p>
          <p className="mt-1 max-w-2xl text-xs text-slate-500 dark:text-slate-400">
            {t("pages.projection.disclaimer")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="application/json"
            className="hidden"
            onChange={(event) => {
              void handleImport(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
          <Button size="sm" variant="secondary" onClick={() => setConfirmLiveImport(true)} isLoading={importingLive}>
            {t("pages.projection.importLive")}
          </Button>
          {tab === "settings" ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => fileRef.current?.click()}>
                {t("pages.projection.importJson")}
              </Button>
              <Button size="sm" variant="ghost" onClick={exportJson}>
                {t("pages.projection.exportJson")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmReset(true)}>
                {t("pages.projection.reset")}
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <div className="panel-card flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
          <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {t("pages.projection.alternatives.current")}
          </label>
          <select
            className="w-full max-w-sm rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            value={workspace.currentId ?? "__unsaved__"}
            onChange={(event) => requestLoad(event.target.value)}
          >
            {workspace.currentId ? null : (
              <option value="__unsaved__" disabled>
                {t("pages.projection.alternatives.unsaved")}
              </option>
            )}
            {workspace.alternatives.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
          {dirty ? (
            <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
              {t("pages.projection.alternatives.dirty")}
            </span>
          ) : currentAlternative ? (
            <span className="text-xs font-medium text-teal-700 dark:text-teal-300">
              {t("pages.projection.alternatives.saved")}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {currentAlternative ? (
            <Button size="sm" variant="secondary" onClick={() => { saveCurrent(); success(t("pages.projection.alternatives.savedToast")); }}>
              {t("pages.projection.alternatives.update")}
            </Button>
          ) : null}
          <Button
            size="sm"
            onClick={() => {
              setSaveName(nextAlternativeName(workspace.alternatives));
              setSaveNotes("");
              setSaveNewOpen(true);
            }}
          >
            {t("pages.projection.alternatives.saveNew")}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setTab("compare")}>
            {t("pages.projection.alternatives.compare")}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-800">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
              tab === key
                ? "bg-teal-600 text-white"
                : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-gray-700"
            }`}
          >
            {t(`pages.projection.tabs.${key}`)}
          </button>
        ))}
      </div>

      {tab === "simulator" ? (
        <SimulatorPanel model={model} result={result} breakEven={breakEven} onChange={setModel} />
      ) : null}

      {tab === "settings" ? (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-1">
            {(["costs", "prices", "portfolio"] as const).map((section) => (
              <button
                key={section}
                type="button"
                onClick={() => setSettingsSection(section)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                  settingsSection === section
                    ? "bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900"
                    : "text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-800"
                }`}
              >
                {t(`pages.projection.tabs.${section}`)}
              </button>
            ))}
          </div>
          {settingsSection === "costs" ? <CostsEditor model={model} onChange={setModel} /> : null}
          {settingsSection === "prices" ? <PricesEditor model={model} onChange={setModel} /> : null}
          {settingsSection === "portfolio" ? (
            <PortfolioEditor
              model={model}
              onChange={setModel}
              liveClinicCount={clinics?.activeClinics ?? null}
            />
          ) : null}
        </div>
      ) : null}

      {tab === "compare" ? (
        <AlternativesPanel
          alternatives={workspace.alternatives}
          currentId={workspace.currentId}
          dirty={dirty}
          comparisonRows={comparisonRows}
          defaultName={nextAlternativeName(workspace.alternatives)}
          onSaveCurrent={() => { saveCurrent(); success(t("pages.projection.alternatives.savedToast")); }}
          onSaveAsNew={(name, notes) => { saveAsNew(name, notes); success(t("pages.projection.alternatives.savedToast")); }}
          onLoad={(id) => { loadAlternative(id); success(t("pages.projection.alternatives.loaded")); }}
          onDuplicate={(id) => { duplicateAlternative(id); success(t("pages.projection.alternatives.duplicated")); }}
          onRename={renameAlternative}
          onDelete={deleteAlternative}
        />
      ) : null}

      <ConfirmModal
        isOpen={confirmLiveImport}
        onClose={() => setConfirmLiveImport(false)}
        onConfirm={handleImportLive}
        title={t("pages.projection.importLive")}
        message={t("pages.projection.importLiveConfirm")}
        confirmText={t("pages.projection.importLive")}
        isLoading={importingLive}
      />

      <ConfirmModal
        isOpen={confirmReset}
        onClose={() => setConfirmReset(false)}
        onConfirm={() => {
          resetToDefaults();
          setConfirmReset(false);
          success(t("pages.projection.resetDone"));
        }}
        title={t("pages.projection.reset")}
        message={t("pages.projection.resetConfirm")}
        variant="danger"
      />

      <ModalShell
        isOpen={saveNewOpen}
        onClose={() => setSaveNewOpen(false)}
        title={t("pages.projection.alternatives.saveNewTitle")}
        subtitle={t("pages.projection.alternatives.saveNewSubtitle")}
        footer={
          <>
            <Button
              onClick={() => {
                saveAsNew(saveName, saveNotes);
                setSaveNewOpen(false);
                success(t("pages.projection.alternatives.savedToast"));
              }}
            >
              {t("pages.projection.alternatives.saveNew")}
            </Button>
            <Button variant="secondary" onClick={() => setSaveNewOpen(false)}>
              {t("common.cancel", { defaultValue: "Cancelar" })}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label={t("pages.projection.alternatives.name")}
            value={saveName}
            onChange={(event) => setSaveName(event.target.value)}
          />
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("pages.projection.alternatives.notes")}
            </span>
            <textarea
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              rows={3}
              value={saveNotes}
              onChange={(event) => setSaveNotes(event.target.value)}
              placeholder={t("pages.projection.alternatives.notesPlaceholder")}
            />
          </label>
        </div>
      </ModalShell>

      <ConfirmModal
        isOpen={Boolean(pendingLoadId)}
        onClose={() => setPendingLoadId(null)}
        onConfirm={() => {
          if (pendingLoadId) {
            loadAlternative(pendingLoadId);
            success(t("pages.projection.alternatives.loaded"));
          }
          setPendingLoadId(null);
        }}
        title={t("pages.projection.alternatives.loadTitle")}
        message={t("pages.projection.alternatives.loadDirty")}
        confirmText={t("pages.projection.alternatives.loadAnyway")}
        variant="danger"
      />
    </div>
  );
};
